import {getLogger} from '@/shared/utils/logUtils'
import {OWN_ROOT, ShareAccess, ShareRoot, StorageRoot} from '../core/storage'

/**
 * The Shared root (decision D1): a virtual root next to My Files listing the folders shared by and
 * with the user. It holds no files of its own, so it is not a {@link StorageRoot}.
 */
export interface SharedIndexRoot {
  kind: 'sharedIndex'
}

export const SHARED_INDEX: SharedIndexRoot = {kind: 'sharedIndex'}

/** Where the Files browser is: My Files, the Shared root, or inside one shared folder. */
export type BrowserRoot = StorageRoot | SharedIndexRoot

/** The root the browser shows files from, or null on the Shared root (which lists shares). */
export const storageRootOf = (root: BrowserRoot): StorageRoot | null =>
  root.kind === 'sharedIndex' ? null : root

/**
 * localStorage key for the root. The folder inside it stays in `fileTable-folderPath`, so the two
 * together persist `{root, path}`; both are cleared with the rest of the user's session keys.
 */
export const FILE_TABLE_ROOT_KEY = 'fileTable-root'

const ACCESS: readonly ShareAccess[] = ['owner', 'edit', 'view']

function parseShareRoot(value: Record<string, unknown>): ShareRoot | null {
  const {id, ownerIdentityId, folderPath, access, name} = value
  if (
    typeof id !== 'string' ||
    !id ||
    typeof ownerIdentityId !== 'string' ||
    !ownerIdentityId ||
    typeof folderPath !== 'string' ||
    !folderPath.endsWith('/') ||
    !ACCESS.includes(access as ShareAccess)
  ) {
    return null
  }
  return {
    kind: 'share',
    id,
    ownerIdentityId,
    folderPath,
    access: access as ShareAccess,
    ...(typeof name === 'string' ? {name} : {}),
  }
}

/** The stored root, or My Files when there is none or it does not parse. */
export function readStoredRoot(): BrowserRoot {
  try {
    const raw = localStorage.getItem(FILE_TABLE_ROOT_KEY)
    if (!raw) {
      return OWN_ROOT
    }
    const value = JSON.parse(raw)
    if (value?.kind === 'sharedIndex') {
      return SHARED_INDEX
    }
    if (value?.kind === 'share') {
      return parseShareRoot(value) ?? OWN_ROOT
    }
  } catch (error) {
    getLogger().error('Failed to parse stored file browser root:', error)
  }
  return OWN_ROOT
}

export function storeRoot(root: BrowserRoot): void {
  try {
    if (root.kind === 'own') {
      // My Files is the default; leaving no key keeps older builds and fresh sessions identical.
      localStorage.removeItem(FILE_TABLE_ROOT_KEY)
    } else {
      localStorage.setItem(FILE_TABLE_ROOT_KEY, JSON.stringify(root))
    }
  } catch (error) {
    getLogger().error('Failed to store file browser root:', error)
  }
}

/** Same root: same kind and, for a shared folder, the same share. */
export function sameRoot(a: BrowserRoot, b: BrowserRoot): boolean {
  if (a.kind !== b.kind) {
    return false
  }
  return a.kind !== 'share' || (b.kind === 'share' && a.id === b.id)
}

/**
 * What a viewer opens for a file at `path` in `root`: in a folder someone else shares, the path in
 * it and the share, so the file is read through the share (never presigned as the user's own key);
 * everywhere else, including the owner's own shared folder, the path in My Files.
 */
export function viewerTarget(
  root: StorageRoot | null | undefined,
  path: string
): {filename: string; share?: ShareRoot} {
  if (root?.kind !== 'share') {
    return {filename: path}
  }
  if (root.access === 'owner') {
    return {filename: `${root.folderPath}${path}`}
  }
  return {filename: path, share: root}
}
